import * as borogove from "borogove";
import { readMessage, decrypt } from "openpgp";
import Picker from "@emoji-mart/react";
import encodeQR from "qr";
import { useRef, useState } from "react";
import {
	Button,
	ChatContainer,
	MessageList,
} from "@chatscope/chat-ui-kit-react";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	FormControlLabel,
	FormGroup,
	ListSubheader,
	Menu,
	MenuItem,
	Popover,
	Switch,
	TextField,
} from "@mui/material";
import { useOutletContext } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faCheck,
	faCircleXmark,
	faFaceSmile,
	faFileImport,
	faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";

import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { AwaitOnce } from "./AwaitOnce";
import { EditableProfile } from "./EditableProfile";
import { confirmDialog } from "./ConfirmDialog";

const timeInFuture = (seconds: number) => {
	return new Date(new Date().getTime() + seconds * 1000).toLocaleString([], {
		timeStyle: "short",
	});
};

export const Account = () => {
	const { active, persistence, client, logout } = useOutletContext<{
		active: boolean;
		persistence: borogove.Persistence;
		client: borogove.Client;
		logout: (completely: boolean) => void;
	}>();
	const importWaitRef = useRef<{
		p: Promise<boolean>;
		next: (v: boolean) => void;
	}>({ p: Promise.resolve(true), next: () => {} });
	const importSettingsRef = useRef<{
		archive?: boolean;
		contacts?: boolean;
		bookmark?: boolean;
	}>({});
	const importerRef = useRef<borogove.Importer | null>(null);
	const [importer, setImporter] = useState<borogove.Importer | null>(null);
	const [importStep, setImportStep] = useState<{
		step: "start" | "wait" | "confirm";
		source?: string;
		contacts?: boolean;
		error?: Error;
	}>({ step: "start" });
	const selfChat = client.getChat(client.accountId());

	const confirmThenLogout = () => {
		confirmDialog({
			message:
				"Do you want to also delete all chats and messages from this device?",
			accept: () => logout(true),
			reject: () => logout(false),
		});
	};

	const uploadAvatar = () => {
		const input = document.createElement("input");
		input.type = "file";
		input.multiple = false;
		input.addEventListener("change", () => {
			if (!input.files || input.files.length < 1) return; // Nothing selected

			client.setPhoto(input.files[0], true);
		});
		input.click();
	};

	const [statusPopover, setStatusPopover] = useState<HTMLElement | null>(null);
	const [statusExpireMenu, setStatusExpireMenu] = useState<HTMLElement | null>(
		null,
	);
	const [statusEmoji, setStatusEmoji] = useState(selfChat?.status?.emoji ?? "");
	const [statusText, setStatusText] = useState(selfChat?.status?.text ?? "");

	return (
		<ChatContainer className={active ? "active" : undefined}>
			<MessageList className="account">
				<MessageList.Content>
					<h1>Account Details</h1>
					<code>{client.accountId()}</code>

					<AvatarWithPlaceholder
						displayName={client.displayName()}
						placeholderUri={selfChat?.getPlaceholder()}
						photoUri={selfChat?.getPhoto()}
						onClick={uploadAvatar}
					/>

					<div className="status">
						<Button
							className="emoji"
							onClick={(e) => setStatusPopover(e.currentTarget)}
						>
							{statusEmoji || <FontAwesomeIcon icon={faFaceSmile} />}
						</Button>
						<input
							type="text"
							value={statusText}
							onChange={(e) => setStatusText(e.target.value)}
							placeholder="Set a custom status"
						/>
						{(statusText || statusEmoji) && (
							<Button
								icon={
									<FontAwesomeIcon
										icon={faCircleXmark}
										onClick={() => {
											setStatusEmoji("");
											setStatusText("");
											client.setStatus(new borogove.Status("", ""));
										}}
									/>
								}
							/>
						)}
						<Button
							icon={<FontAwesomeIcon icon={faCheck} />}
							onClick={(e) => {
								setStatusExpireMenu(e.currentTarget);
							}}
						/>
						<Popover
							open={!!statusPopover}
							anchorEl={statusPopover}
							onClose={() => setStatusPopover(null)}
						>
							<Picker
								data={async () => ({ ...(await import("@emoji-mart/data")) })}
								categories={[
									"frequent",
									"activity",
									"places",
									"objects",
									"people",
									"nature",
									"foods",
									"symbols",
									"flags",
								]}
								maxFrequentRows={1}
								onEmojiSelect={(emoji: { native: string }) => {
									setStatusEmoji(emoji.native);
									setStatusPopover(null);
								}}
							/>
						</Popover>
						<Menu
							open={!!statusExpireMenu}
							anchorEl={statusExpireMenu}
							onClose={() => setStatusExpireMenu(null)}
						>
							<ListSubheader>Set status for</ListSubheader>
							<MenuItem
								onClick={() => {
									client.setStatus(
										new borogove.Status(statusEmoji, statusText),
										86400,
									);
									setStatusExpireMenu(null);
								}}
							>
								24 hours (tomorrow at {timeInFuture(86400)})
							</MenuItem>
							<MenuItem
								onClick={() => {
									client.setStatus(
										new borogove.Status(statusEmoji, statusText),
										14400,
									);
									setStatusExpireMenu(null);
								}}
							>
								4 hours ({timeInFuture(14400)})
							</MenuItem>
							<MenuItem
								onClick={() => {
									client.setStatus(
										new borogove.Status(statusEmoji, statusText),
										3600,
									);
									setStatusExpireMenu(null);
								}}
							>
								1 hours ({timeInFuture(3600)})
							</MenuItem>
							<MenuItem
								onClick={() => {
									client.setStatus(
										new borogove.Status(statusEmoji, statusText),
										1800,
									);
									setStatusExpireMenu(null);
								}}
							>
								30 minutes ({timeInFuture(1800)})
							</MenuItem>
						</Menu>
					</div>

					<AwaitOnce
						key={client.accountId()}
						resolve={async () =>
							await selfChat
								?.getMemberDetails([client.accountId()])
								.then((members) => members[0]?.profile(client))
						}
						fallback={<CircularProgress />}
					>
						{(profile) =>
							profile && (
								<EditableProfile
									initial={profile}
									saveProfile={(profile, pub) =>
										client.setProfile(profile, pub)
									}
								/>
							)
						}
					</AwaitOnce>

					<Button
						icon={<FontAwesomeIcon icon={faFileImport} />}
						onClick={() => {
							const importer = new borogove.Importer(client.accountId());

							importer.onAccount = (account) => {
								let next!: (value: boolean) => void;
								const p = new Promise<boolean>((resolve) => {
									next = resolve;
								});
								importWaitRef.current = { p, next };
								setImportStep({
									step: "confirm",
									source: account,
									contacts: true,
								});
								importSettingsRef.current = {};
							};

							importer.onChannel = (channel) => {
								let next!: (value: boolean) => void;
								const p = new Promise<boolean>((resolve) => {
									next = resolve;
								});
								importWaitRef.current = { p, next };
								setImportStep({ step: "confirm", source: channel });
								importSettingsRef.current = {};
							};

							importer.onChat = (source, availableChat) => {
								if (!importSettingsRef.current.contacts) return;

								const chat = client.startChat(availableChat);
								if (importSettingsRef.current.bookmark) chat.bookmark();
							};

							setImportStep({ step: "start" });
							importerRef.current = importer;
							setImporter(importer);
						}}
					>
						Import account data
					</Button>

					<Dialog open={!!importer}>
						{importStep.step === "start" && (
							<>
								<DialogContent>
									<DialogContentText>Choose file to import</DialogContentText>
									<form
										id="import-step-form"
										onSubmit={async (e) => {
											e.preventDefault();
											const activeImporter = importerRef.current;
											if (!activeImporter) return;

											setImportStep({ step: "wait" });
											console.time("import");

											let count = 0;
											let batch: borogove.ChatMessage[] = [];
											activeImporter.onMessage = (source, item) => {
												if (!importSettingsRef.current.archive) return;

												count++;
												if (count % 1000 == 0) {
													console.log(count);
													console.timeLog("import");
												}
												if (item.parsed.message) {
													batch.push(item.parsed.message);

													if (batch.length < 1000) {
														return;
													}

													const toWrite = batch;
													batch = [];
													importWaitRef.current = {
														p: (async () => {
															try {
																await persistence.storeMessages(
																	client.accountId(),
																	toWrite,
																);
															} catch (e) {
																console.error(
																	e,
																	item.parsed.message.stanza.toString(),
																	item.parsed.message,
																);
															}
															return true;
														})(),
														next: () => {},
													};
												}
											};

											try {
												const password = e.currentTarget.password.value;
												const message = await readMessage({
													binaryMessage: e.currentTarget.file.files[0].stream(),
												});
												const { data } = await decrypt({
													message,
													passwords: [password],
												});

												for await (const chunk of data) {
													if (importSettingsRef.current.archive === undefined) {
														for (let i = 0; i < chunk.length; i++) {
															activeImporter.write(chunk.slice(i, i + 1));
															const keepGoing = await importWaitRef.current.p;
															if (!keepGoing) {
																setImporter(null);
																return;
															}
														}
													} else {
														activeImporter.write(chunk);
													}
													const keepGoing = await importWaitRef.current.p;
													if (!keepGoing) {
														setImporter(null);
														return;
													}
												}
												// Anything left in the batch to commit
												await persistence.storeMessages(
													client.accountId(),
													batch,
												);
											} catch (e) {
												setImportStep({ step: "start", error: e as Error });
												return;
											}
											console.timeEnd("import");
											setImporter(null);
										}}
									>
										<FormGroup>
											<input type="file" name="file" />
											<TextField
												name="password"
												label="Password"
												type="password"
												error={!!importStep.error}
												helperText={importStep.error && "" + importStep.error}
											/>
										</FormGroup>
									</form>
								</DialogContent>
								<DialogActions>
									<Button onClick={() => setImporter(null)}>Cancel</Button>
									<Button type="submit" form="import-step-form">
										Start Import
									</Button>
								</DialogActions>
							</>
						)}

						{importStep.step === "wait" && (
							<DialogContent>
								<CircularProgress />
							</DialogContent>
						)}
						{importStep.step === "confirm" && (
							<>
								<DialogContent>
									<DialogContentText>
										{"What do want to import from " +
											importStep.source +
											" to " +
											client.accountId() +
											" ?"}
									</DialogContentText>
									<form
										id="import-step-form"
										onSubmit={(e) => {
											e.preventDefault();
											if (
												!e.currentTarget.archive.checked &&
												!e.currentTarget.contacts?.checked
											) {
												if (importStep.source) {
													importer?.ignoreSource(importStep.source);
												}
											}
											importSettingsRef.current = {
												archive: e.currentTarget.archive.checked,
												contacts: e.currentTarget.contacts?.checked,
												bookmark: e.currentTarget.bookmark?.checked,
											};
											importWaitRef.current.next(true);
											setImportStep({ step: "wait" });
										}}
									>
										<FormGroup>
											<FormControlLabel
												control={<Switch name="archive" defaultChecked />}
												label="Message History"
											/>
											{importStep.contacts && (
												<FormControlLabel
													control={<Switch name="contacts" defaultChecked />}
													label="Contacts"
												/>
											)}
											{importStep.contacts && (
												<FormControlLabel
													control={<Switch name="bookmark" defaultChecked />}
													label="Send contacts to server"
												/>
											)}
										</FormGroup>
									</form>
								</DialogContent>
								<DialogActions>
									<Button onClick={() => importWaitRef.current.next(false)}>
										Cancel
									</Button>
									<Button type="submit" form="import-step-form">
										Continue
									</Button>
								</DialogActions>
							</>
						)}
					</Dialog>

					<Button
						icon={<FontAwesomeIcon icon={faRightFromBracket} />}
						onClick={confirmThenLogout}
					>
						Remove this account
					</Button>

					<div
						className="qrcode"
						dangerouslySetInnerHTML={{
							__html: encodeQR(
								"xmpp:" + encodeURIComponent(client.accountId()),
								"svg",
								{ ecc: "high" },
							),
						}}
					/>
				</MessageList.Content>
			</MessageList>
		</ChatContainer>
	);
};
